import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';
import { useAuth } from '@/providers/AuthProvider';
import {
  useUserChats,
  useChatMessages,
  useSendMessage,
  useChatSocket,
  ChatMessage,
  ChatSummary,
} from '@/services/chat';
import { useProfile } from '@/services/profiles';
import { getChatUserId } from '@/utils/chatUser';
import styles from './ChatPage.module.css';

const formatTimestamp = (timestamp: number) => {
  const date = new Date(timestamp);
  return `${date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })} · ${date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

const formatUserDisplayName = (profile: any) => {
  if (profile?.name?.trim()) {
    return profile.name;
  }
  if (profile?.email?.trim()) {
    return profile.email;
  }
  return '<UnknownUser>';
};

const ChatListItem = ({
  chat,
  isActive,
  onClick,
}: {
  chat: ChatSummary;
  isActive: boolean;
  onClick: () => void;
}) => {
  const { data: otherUserProfile } = useProfile(chat.otherUserId, {
    enabled: Boolean(chat.otherUserId),
  });

  const displayName = formatUserDisplayName(otherUserProfile);
  const buttonLabel =
    displayName === '<UnknownUser>' ? (chat.otherUserId ?? 'Unknown user') : displayName;

  return (
    <li key={chat.chatRoomId}>
      <button
        type="button"
        className={clsx(styles.chatListItem, {
          [styles.chatListItemActive]: isActive,
        })}
        onClick={onClick}
      >
        <span className={styles.chatName}>{buttonLabel}</span>
        <span className={styles.chatMeta}>
          {chat.unreadCount > 0 && <strong>{`${chat.unreadCount} unread · `}</strong>}
          {formatTimestamp(chat.lastMessageAt)}
        </span>
      </button>
    </li>
  );
};

const MessageBubble = ({
  message,
  isOwnMessage,
  partnerId,
  currentUserId,
}: {
  message: ChatMessage;
  isOwnMessage: boolean;
  partnerId: string | null;
  currentUserId: string;
}) => {
  // For own messages, we don't need to fetch profile - just show "You"
  // For partner messages, fetch their profile
  const { data: senderProfile } = useProfile(isOwnMessage ? '' : message.senderId, {
    enabled: !isOwnMessage && Boolean(message.senderId),
  });

  const senderName = isOwnMessage ? 'Me' : formatUserDisplayName(senderProfile);

  return (
    <article
      className={clsx(styles.messageBubble, {
        [styles.messageSelf]: isOwnMessage,
      })}
    >
      <div className={styles.messageHeader}>
        <span className={styles.messageSender}>{senderName}</span>
      </div>
      <p>{message.message}</p>
      <time dateTime={new Date(message.timestamp).toISOString()}>
        {formatTimestamp(message.timestamp)}
      </time>
    </article>
  );
};

const ChatPage = () => {
  const { userId: targetUserId } = useParams<{ userId: string }>();
  const auth = useAuth();
  const userId = getChatUserId(auth.user) || 'guest-user';
  const {
    data: chatsData,
    isLoading: chatsLoading,
    isError: chatsError,
  } = useUserChats(userId, { limit: 20 });
  const chats = useMemo(() => chatsData?.chats ?? [], [chatsData?.chats]);

  const [activeChatId, setActiveChatId] = useState<string | null>(null);

  useEffect(() => {
    if (targetUserId && chats.length) {
      // If we have a target user, try to find an existing chat with them
      const existingChat = chats.find((chat) => chat.otherUserId === targetUserId);
      if (existingChat) {
        setActiveChatId(existingChat.chatRoomId);
        return;
      }
    }

    if (!activeChatId && chats.length) {
      setActiveChatId(chats[0].chatRoomId);
    }
  }, [activeChatId, chats, targetUserId]);

  const activeChat: ChatSummary | undefined = useMemo(
    () => chats.find((chat) => chat.chatRoomId === activeChatId) ?? undefined,
    [activeChatId, chats],
  );

  const partnerId = activeChat?.otherUserId ?? targetUserId ?? null;

  const { data: partnerProfile } = useProfile(partnerId ?? '', {
    enabled: Boolean(partnerId),
  });

  const {
    data: messagesData,
    isLoading: messagesLoading,
    isError: messagesError,
    refetch: refetchMessages,
  } = useChatMessages(userId, partnerId ?? '', {
    enabled: Boolean(partnerId),
    count: 100,
  });

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (messagesData?.messages) {
      setMessages(messagesData.messages);
    }
  }, [messagesData?.messages]);

  useEffect(() => {
    const node = messagesEndRef.current;
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length]);

  const socket = useChatSocket({
    userId,
    onMessage: (message) => {
      // Accept messages for the active chat OR messages involving the current partner
      const shouldAcceptMessage =
        message.chatRoomId === activeChatId ||
        (partnerId && (message.senderId === partnerId || message.senderId === userId));

      if (shouldAcceptMessage) {
        setMessages((current) => [...current, message]);

        // If this is a new chat, update the activeChatId
        if (!activeChatId && message.chatRoomId) {
          setActiveChatId(message.chatRoomId);
        }
      }
    },
  });

  // Fallback: if socket is disconnected, increase polling frequency
  useEffect(() => {
    if (socket && !socket.connected && partnerId) {
      const interval = setInterval(() => {
        refetchMessages();
      }, 2000); // Poll every 2 seconds when disconnected
      return () => clearInterval(interval);
    }
  }, [socket?.connected, partnerId, refetchMessages]);

  const sendMessage = useSendMessage();
  const [draft, setDraft] = useState('');

  const messagesContent = useMemo(() => {
    if (messagesError) {
      return <p className={styles.messagesNotice}>Could not load messages.</p>;
    }
    if (messagesLoading && !messages.length) {
      return <p className={styles.messagesNotice}>Loading messages…</p>;
    }
    return messages.map((message) => {
      const isOwnMessage = message.senderId === userId;

      return (
        <MessageBubble
          key={message.id}
          message={message}
          isOwnMessage={isOwnMessage}
          partnerId={partnerId}
          currentUserId={userId}
        />
      );
    });
  }, [messages, messagesError, messagesLoading, userId, partnerId]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!partnerId || draft.trim().length === 0) {
      return;
    }
    const optimistic: ChatMessage = {
      id: `optimistic-${Date.now()}`,
      senderId: userId,
      message: draft,
      timestamp: Date.now(),
      chatRoomId: activeChatId ?? '',
    };
    setMessages((prev) => [...prev, optimistic]);
    setDraft('');
    sendMessage.mutate(
      {
        senderId: userId,
        recipientId: partnerId,
        message: optimistic.message,
      },
      {
        onError: () => {
          setMessages((prev) => prev.filter((message) => message.id !== optimistic.id));
        },
      },
    );
  };

  const emptyState = chatsLoading ? 'Loading conversations…' : 'No active chats yet.';

  return (
    <div className={styles.page}>
      <Helmet>
        <title>Chat • AwkHerd</title>
      </Helmet>
      <header className={styles.header}>
        <div>
          <h1>Chat</h1>
          <p>Stay in sync with your AwkHerd connections in real time.</p>
        </div>
      </header>
      <div className={styles.layout}>
        <aside className={styles.sidebar} aria-label="Conversations">
          <div className={styles.sidebarHeader}>
            <h2>Conversations</h2>
            <span className={styles.sidebarMeta} aria-live="polite">
              {chatsLoading
                ? 'Loading…'
                : `${chats.length} active ${chats.length === 1 ? 'chat' : 'chats'}`}
            </span>
          </div>
          <ul className={styles.chatList}>
            {chats.map((chat) => (
              <ChatListItem
                key={chat.chatRoomId}
                chat={chat}
                isActive={chat.chatRoomId === activeChatId}
                onClick={() => setActiveChatId(chat.chatRoomId)}
              />
            ))}
          </ul>
          {chatsError && (
            <p className={styles.sidebarNotice}>Unable to load chats right now.</p>
          )}
          {!chats.length && !chatsLoading && (
            <p className={styles.sidebarNotice}>{emptyState}</p>
          )}
        </aside>

        <section className={styles.chatPane} aria-live="polite">
          {partnerId ? (
            <>
              <header className={styles.chatPaneHeader}>
                <div>
                  <h2>{formatUserDisplayName(partnerProfile)}</h2>
                  <span>{messages.length} messages</span>
                </div>
              </header>
              <div className={styles.messages}>
                {messagesContent}
                <div ref={messagesEndRef} />
              </div>
              <form className={styles.composer} onSubmit={handleSubmit}>
                <textarea
                  id="chat-message"
                  value={draft}
                  placeholder="Write a message"
                  aria-label="Message"
                  onChange={(event) => setDraft(event.target.value)}
                  maxLength={1000}
                  rows={3}
                />
                <div className={styles.composerFooter}>
                  <span>{draft.length}/1000</span>
                  <button type="submit" disabled={draft.trim().length === 0}>
                    Send
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className={styles.emptyPane}>
              <h2>Select a conversation</h2>
              <p>Start chatting by choosing a contact from the list.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ChatPage;
